import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { RequestService } from 'src/app/services/request.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

interface WeatherCondition {
  last_24_hours: {
    min_hum_out: number;
    max_hum_out: number;
    min_temp_out: number;
    max_temp_out: number;
    total_rainfall_mm: number;
    min_wind_speed_hi: number;
    max_wind_speed_hi: number;
  };
  last_week: {
    min_hum_out: number;
    max_hum_out: number;
    min_temp_out: number;
    max_temp_out: number;
    total_rainfall_mm: number;
    min_wind_speed_hi: number;
    max_wind_speed_hi: number;
  };
  last_two_weeks: {
    min_hum_out: number;
    max_hum_out: number;
    min_temp_out: number;
    max_temp_out: number;
    total_rainfall_mm: number;
    min_wind_speed_hi: number;
    max_wind_speed_hi: number;
    rainfall_week_1: number;
    rainfall_week_2: number;
  };
}

@Component({
  selector: 'app-pests',
  templateUrl: './pests.component.html',
  styleUrls: ['./pests.component.scss']
})

export class PestsComponent implements OnInit {
  selectedPlot: any;
  thermostats: any[] = [];
  airQualityData: any;
  securityPanel: any[] = [];
  locks: any[] = [];
  reportDiseases: any[] = [];
  mappedPlots: any[] = [];
  mappedPlotsFert: any[] = [];
  plots: any[] = [];
  plots2: any[] = [];
  pestFertilizer: any[] = [];
  pestDisease: any[] = [];
  weatherConditions: WeatherCondition[] = [];
  aggregatedData: any[] = [];
  allData: any[] = [];
  cropsFertilisier: any[] = [];
  cropsDiseases: any[] = [];
  diseasesStatus: boolean = false;
  groupedData: any[] = [];
  plotsCodes: any[] = [];
  dateTime: any;
  mappedWeather: any[] = [];


  constructor(public translate: TranslateService, private req: RequestService<any>) { }

  ngOnInit(): void {
    this.initPests();
  }

  initPests() {
    this.req.getPlotsPests().subscribe((res: any) => {
      this.plots2 = res
      const plotCode = res[0].plot_code
      const plotId = res[0]._id
      const plotLocation = res[0].location
      this.selectPlot(plotCode, plotId, plotLocation)
    });
    this.dateTime = new Date();
  }

  plotHasDetectedDisease(plot: any): boolean {
    return plot.diseases.some((disease: any) =>
      disease.report && disease.report.some((report: any) => report.detected)
    );
  }

  plotHasDetectedFertiliser(plot: any): boolean {
    plot.fertilisers.forEach((fert: any) => {
      if (fert.report && Array.isArray(fert.report)) {
        fert.report.sort((a: any, b: any) => Number(b.allowed?.allowed) - Number(a.allowed?.allowed))
      }
    })
    return plot.fertilisers.some((fertiliser: any) =>
      fertiliser.report && fertiliser.report.some((report: any) => report.allowed)
    );
  }
  plotHasDetectedPesticides(plot: any): boolean {
    plot.pesticides.forEach((pest: any) => {
      if (pest.report && Array.isArray(pest.report)) {
        pest.report.sort((a: any, b: any) => Number(b.allowed?.allowed) - Number(a.allowed?.allowed));
      }
    });
    return plot.pesticides.some((pest: any) =>
      pest.report && pest.report.some((report: any) => report.allowed?.allowed === true)
    );
  }


  selectPlot(plot: any, plotSelect: any, plotLocation?: any): void {
    this.selectedPlot = plotLocation;
    const firstPlotSelect = plotSelect ? plotSelect : this.plots2[0];
    const plotCode = plot;
    this.groupedData = [];
    this.plotsCodes = [];
    this.weatherConditions = [];
    this.mappedWeather = [];
    this.plotsCodes.push(firstPlotSelect.crop);
    this.req.getPestsFertilizer(plotCode).subscribe((resFertiliser) => {
      resFertiliser.forEach((el: any) => {
        if (el.report.length) {
          el.report.forEach((item: any) => {
            this.weatherConditions.push(item.conditions);
          });
        }
      });
      this.req.getPestsDiseases(plotCode).subscribe((resDiseases) => {
        if (this.weatherConditions.length === 0) {
          resDiseases.forEach((el: any) => {
            if (el.report.length) {
              el.report.forEach((item: any) => {
                this.weatherConditions.push(item.conditions);
              });
            }
          });
        }
        this.req.getPestsPesticides(plotCode).subscribe((resPesticides: any) => {
          if (this.weatherConditions.length === 0) {
            resPesticides.forEach((el: any) => {
              if (el.report.length) {
                el.report.forEach((item: any) => {
                  this.weatherConditions.push(item.conditions);
                });
              }
            });
          }
          this.groupedData = this.groupData(
            firstPlotSelect.crop,
            resFertiliser,
            resDiseases,
            resPesticides
          );
          if (this.weatherConditions.length) {
            this.mappedWeather = Object.entries(this.weatherConditions[0]).map(([key, value]) => ({
              period: key,
              data: value
            }));
          }
        });
      });
    });
  }

  getTitle(period: string): string {
    const map: any = {
      last_24_hours: 'PEST.WEATHER_24',
      last_week: 'PEST.WEATHER_WEEK',
      last_two_weeks: 'PEST.WEATHER_2_WEEKS',
      next_24_hours: 'PEST.WEATHER_ESTIMATE_48H'
    };
    return map[period] || period;
  }

  getIcon(period: string): string {
    const icons: any = {
      last_24_hours: 'fas fa-clock',
      last_week: 'fas fa-calendar-week',
      last_two_weeks: 'fas fa-calendar-alt',
      next_24_hour: 'fas fa-calendar-alt'
    };
    return icons[period] || 'fas fa-question-circle';
  }

  getRainfallMin(period: string, data: any): string {
    if (period === 'last_two_weeks' || period === 'next_24_hour') {
      return data?.rainfall_week_2?.toFixed(2) || '-';
    }
    return '-';
  }

  getRainfallMax(period: string, data: any): string {
    return data?.total_rainfall_mm?.toFixed(2) || '-';
  }



  groupData(plots: any[], fertiliserData: any[], diseasesData: any[], pesticideData: any[]): any[] {
    return plots.map((plot) => {
      const fertilisers = fertiliserData.filter(
        (fertiliser) => fertiliser.crop_id === plot.crop_info_id
      );
      const diseases = diseasesData.filter(
        (pesticide) => pesticide.crop_id === plot.crop_info_id
      );
      const pesticides = pesticideData.filter(
        (disease) => disease.crop_id === plot.crop_info_id
      );
      return {
        crop_info_id: plot.crop_info_id,
        fertilisers: fertilisers,
        diseases: diseases,
        pesticides: pesticides
      };
    });
  }
}
